import { useSSRHook } from "ssr-hook";
import { getFileName, toBase64, useHead } from "../services/common";
import { Download } from "../../types";
import { FileIcon, InputDate, Loading, Remove } from "../components";
import moment from "moment";
import { uniq } from "lodash";
import { useRef, useState } from "react";
import { useUser } from "../User";
import Icon from "@mdi/react";
import { mdiFile, mdiPencil, mdiPlus } from "@mdi/js";
import Modal from "react-responsive-modal";
import { api } from "../services/api";

const emptyDownload: Download = {
    _id: "",
    name: "",
    folder: "others",
    date: moment().format("YYYY-MM-DD"),
    file: {
        type: "EASY_DB_FILE",
        url: "",
    },
}

export default function Documents() {
    const user = useUser();

    useHead({
        title: "Důležité dokumenty ke stažení | T.J. Sokol Poniklá",
        description: "Oficiální dokumenty T.J. Sokol Poniklá – stanovy, výroční zprávy, přihlášky do oddílů a další důležité informace ke stažení."
    });

    const [downloads, error, isLoading, reload] = useSSRHook<Download[]>(`/api/downloads`);

    const [reportYear, setReportYear] = useState(moment().format("YYYY"));

    const inputRef = useRef<HTMLInputElement>(null);
    const [editDownload, setEditDownload] = useState<null | Download>(null);
    const [message, setMessage] = useState("");

    const statutes = (downloads || []).filter(d => d.folder === "statutes");
    const others = (downloads || []).filter(d => d.folder === "others");
    const reportsAll = (downloads || []).filter(d => d.folder === "reports");

    const reportsYears = uniq(reportsAll.map(d => moment(d.date).format("YYYY"))).sort().reverse();

    const reports = reportsAll.filter(d => moment(d.date).format("YYYY") === reportYear);

    async function handleSave() {
        if (!editDownload) return;

        if (editDownload._id === "") {
            const [, error] = await api.addDownload(editDownload);
            if (error) {
                setMessage("Přidání souboru selhalo");
                return;
            }
            setMessage("Soubor přidán");
        } else {
            const [, error] = await api.updateDownload(editDownload);
            if (error) {
                setMessage("Ukládání změn selhalo");
                return;
            }
            setMessage("Soubor úspěšně uložen")
        }

        reload();
        setEditDownload(null);
    }

    async function handleRemove(downloadId: string) {
        await api.removeDownload({ downloadId });
        reload();
    }

    return <>
        <div className="header">
            <h1>Dokumenty</h1>
        </div>
        <section className="width-max">
            <div className="h-tag left">Dokumenty ke stažení</div>

            {user.isAdmin && <div className="admin">
                <button onClick={() => inputRef.current?.click()}><Icon path={mdiPlus} size={1} /> Přidat dokument</button>

                <input
                    className="hidden"
                    ref={inputRef}
                    type="file"
                    onChange={async (e) => {
                        const file = e.target.files?.[0] as File;
                        if (!file) return;

                        const url = await toBase64(file);

                        setMessage("");
                        setEditDownload({
                            ...(editDownload || emptyDownload),
                            name: file.name,
                            file: { type: "EASY_DB_FILE", url },
                        });
                    }}
                />

                <Modal
                    open={editDownload !== null}
                    onClose={() => setEditDownload(null)}
                    center
                    styles={{ modal: { width: "500px" } }}
                >
                    {message && <p className="info">{message}</p>}
                    {editDownload && <>
                        <h2>{editDownload._id === "" ? "Přidat" : "Upravit"} soubor</h2>
                        <button className="center" onClick={() => inputRef.current?.click()}>
                            <Icon path={mdiFile} size={1} />Nahrát jiný soubor
                        </button>
                        <p className="info">Soubor může mít maximálně 10MB!</p>

                        <select value={editDownload.folder} onChange={(e) => setEditDownload({ ...editDownload, folder: e.target.value as Download["folder"] })}>
                            <option value="statutes">Stanovy</option>
                            <option value="others">Ostatní dokumenty</option>
                            <option value="reports">Zápisy a výroční zprávy</option>
                        </select>
                        <InputDate
                            value={editDownload.date}
                            onChange={(date) => setEditDownload({ ...editDownload, date })}
                        />
                        <input
                            type="text"
                            placeholder="Název"
                            value={editDownload.name}
                            onChange={(e) => setEditDownload({ ...editDownload, name: e.target.value })}
                        />

                        <div className="buttons">
                            <button onClick={handleSave}>Uložit soubor</button>
                        </div>
                    </>}
                </Modal>
            </div>}

            {isLoading && <Loading />}
            {error && <p className="alert">Akce se nepodařilo načíst. Zkuste to prosím později.</p>}
            {downloads && <div className="documents">
                <div>
                    <div className="box">
                        <h2>Stanovy</h2>
                        <div className="h2-line" />

                        {statutes.length === 0 && <p className="info">Zatím zde nejsou žádné soubory ke stažení.</p>}
                        {statutes.length > 0 && <ul className="list-download">
                            {statutes.map(d => <li key={d._id}>
                                <a href={d.file.url} target="__blank" download={getFileName(d.name, d.file.url)}>
                                    <FileIcon url={d.file.url} size={1} />
                                    {d.name}
                                </a>
                                {user.isAdmin && <>
                                    <button className="icon" onClick={() => setEditDownload(d)}>
                                        <Icon path={mdiPencil} size={1} />
                                    </button>
                                    <Remove onRemove={() => handleRemove(d._id)} />
                                </>}
                            </li>)}
                        </ul>}
                    </div>
                    <div className="box">
                        <h2>Ostatní dokumenty</h2>
                        <div className="h2-line" />

                        {others.length === 0 && <p className="info">Zatím zde nejsou žádné soubory ke stažení.</p>}
                        {others.length > 0 && <ul className="list-download">
                            {others.map(d => <li key={d._id}>
                                <a href={d.file.url} target="__blank" download={getFileName(d.name, d.file.url)}>
                                    <FileIcon url={d.file.url} size={1} />
                                    {d.name}
                                </a>
                                {user.isAdmin && <>
                                    <button className="icon" onClick={() => setEditDownload(d)}>
                                        <Icon path={mdiPencil} size={1} />
                                    </button>
                                    <Remove onRemove={() => handleRemove(d._id)} />
                                </>}
                            </li>)}
                        </ul>}
                    </div>
                </div>
                <div>
                    <div className="box">
                        <h2>Zápisy a výroční zprávy</h2>
                        <div className="h2-line" />
                        <div className="button-select">
                            {reportsYears.map(year => <button key={year} className={reportYear === year ? "active" : ""} onClick={() => setReportYear(year)}>
                                {year}
                            </button>)}
                        </div>

                        {reports.length === 0 && <p className="info">Zatím zde nejsou žádné soubory ke stažení.</p>}
                        {reports.length > 0 && <ul className="list-download">
                            {reports.map(d => <li key={d._id}>
                                <a href={d.file.url} target="__blank" download={getFileName(d.name, d.file.url)}>
                                    <FileIcon url={d.file.url} size={1} />
                                    {d.name}
                                </a>
                                {user.isAdmin && <>
                                    <button className="icon" onClick={() => setEditDownload(d)}>
                                        <Icon path={mdiPencil} size={1} />
                                    </button>
                                    <Remove onRemove={() => handleRemove(d._id)} />
                                </>}
                            </li>)}
                        </ul>}
                    </div>
                </div>
            </div>}
        </section>
    </>;
}